import { useEffect, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { api } from "@/lib/oraya-api";
import { Seo, Reveal, Overline } from "@/components/site/primitives";
import { OButton } from "@/components/site/OButton";
import { ApplyModal } from "@/components/site/ApplyModal";
import { IMG } from "@/data/site";

export default function Careers() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [applyJob, setApplyJob] = useState(null);

  useEffect(() => {
    api.get("/careers").then(({ data }) => setJobs(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div className="pt-32 pb-32">
      <Seo title="Careers — ORAYA" description="Build what comes next. Join ORAYA in building intelligent systems for complex real-world industries." path="/careers" />

      <section className="relative overflow-hidden py-16 md:py-24 border-b border-hairline/[0.06]">
        <div className="absolute inset-0 opacity-40">
          <img src={IMG.office} alt="" aria-hidden="true" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-surface via-surface/80 to-surface/40" />
        </div>
        <div className="oraya-container relative z-10">
          <Reveal>
            <Overline>Careers</Overline>
            <h1 className="display text-5xl sm:text-6xl font-light tracking-tight text-gradient-mist mt-6">Build what comes next.</h1>
            <p className="text-lg text-subtle mt-6 max-w-2xl leading-relaxed">
              ORAYA is building intelligent systems for complex real-world industries. We're looking for
              precise, ambitious people who want to build systems, not features.
            </p>
          </Reveal>
        </div>
      </section>

      <div className="oraya-container mt-16">
        <Overline className="mb-8 block">Open positions</Overline>
        {loading && <p className="text-subtle" data-testid="careers-loading">Loading…</p>}
        {!loading && jobs.length === 0 && (
          <div className="glass-medium rounded-2xl p-10 text-center" data-testid="careers-empty">
            <p className="text-ink text-lg">No open positions right now.</p>
            <p className="text-subtle mt-2">We're always meeting exceptional people.</p>
            <div className="mt-6"><OButton to="/contact" variant="secondary">Introduce yourself</OButton></div>
          </div>
        )}

        <div className="flex flex-col gap-3">
          {jobs.map((j) => (
            <Reveal key={j.id}>
              <div className="glass-medium rounded-2xl overflow-hidden" data-testid={`career-${j.id}`}>
                <button
                  onClick={() => setOpen(open === j.id ? null : j.id)}
                  data-testid={`career-toggle-${j.id}`}
                  className="w-full flex items-center justify-between gap-4 p-6 md:p-8 text-left hover:bg-hairline/[0.02] transition-colors"
                >
                  <div>
                    <h3 className="display text-xl md:text-2xl font-medium text-ink">{j.title}</h3>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-subtle mt-2">
                      <span>{j.department}</span><span>·</span><span>{j.location}</span><span>·</span><span>{j.employment_type}</span>
                    </div>
                  </div>
                  <ArrowUpRight className={`w-5 h-5 text-subtle transition-transform duration-300 ${open === j.id ? "rotate-90" : ""}`} />
                </button>
                {open === j.id && (
                  <div className="px-6 md:px-8 pb-8 border-t border-hairline/[0.06] pt-6">
                    <p className="text-subtle leading-relaxed">{j.description}</p>
                    {j.responsibilities?.length > 0 && (
                      <div className="mt-6">
                        <Overline>Responsibilities</Overline>
                        <ul className="mt-3 space-y-2">
                          {j.responsibilities.map((r, i) => <li key={i} className="text-subtle flex gap-3"><span className="text-oraya-accent">—</span>{r}</li>)}
                        </ul>
                      </div>
                    )}
                    {j.requirements?.length > 0 && (
                      <div className="mt-6">
                        <Overline>Requirements</Overline>
                        <ul className="mt-3 space-y-2">
                          {j.requirements.map((r, i) => <li key={i} className="text-subtle flex gap-3"><span className="text-oraya-accent">—</span>{r}</li>)}
                        </ul>
                      </div>
                    )}
                    <div className="mt-8"><button onClick={() => setApplyJob(j)} data-testid={`career-apply-${j.id}`} className="group inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium bg-invert text-invert-ink hover:opacity-90 transition-opacity">Apply for this role <ArrowUpRight className="w-4 h-4" /></button></div>
                  </div>
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      {applyJob && <ApplyModal job={applyJob} onClose={() => setApplyJob(null)} />}
    </div>
  );
}
